import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import euDiseaseService from '../../../services/euDiseaseService';
import { biologicalEntityService } from '../../../services/firebaseService';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step4BiologicalEntities = ({ stepData, onComplete, onValidation, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    if (structure?.organizations && structure.organizations.length > 0) {
      setOrganizationId(structure.organizations[0].id);
    }
  }, [structure]);

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const entitiesData = await biologicalEntityService.getAll();
        setEntities(entitiesData || []);
        if ((entitiesData || []).length >= 5) {
          onComplete({ entities: entitiesData.length, requirementsMet: true });
        }
      } catch (error) {
        console.error('Error loading entities:', error);
      }
    };
    loadEntities();
  }, []);

  const handleImportEUDiseases = async () => {
    if (!organizationId) {
      notify.error(t('step4.notifications.missingOrganization'), t('step4.notifications.missingOrganizationTitle'));
      return;
    }

    setLoading(true);
    try {
      const results = await euDiseaseService.importEUDiseases(organizationId);
      setEntities(prev => [...prev, ...(results.created || [])]);
      notify.success(t('step4.notifications.importedSuccess', { count: results.created?.length || 0 }), t('step4.notifications.importedSuccessTitle'));
      if ((results.created || []).length >= 5) {
        onComplete({ entities: entities.length + (results.created?.length || 0), requirementsMet: true });
      }
    } catch (error) {
      console.error('Error importing EU diseases:', error);
      notify.error(t('step4.notifications.importFailed'), t('step4.notifications.importFailedTitle'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step4.title')}</h3>
        <p>{t('step4.description')}</p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step4.completeStep1Warning')}
        </div>
      )}

      <div className="quick-setup-section">
        <div className="quick-setup-option" style={{ cursor: loading || !organizationId ? 'not-allowed' : 'pointer', opacity: loading || !organizationId ? 0.6 : 1 }}>
          <div style={{ textAlign: 'center' }}>
            <h3>{t('step4.importTitle')}</h3>
            <p>{t('step4.importDescription')}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleImportEUDiseases}
              disabled={loading || !organizationId}
              style={{ marginTop: '16px' }}
            >
              {loading ? t('step4.importing') : t('step4.importButton')}
            </button>
          </div>
        </div>
      </div>

      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step4.entitiesTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${entities.length >= 5 ? 'met' : 'pending'}`}>
            {entities.length >= 5 ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step4.entitiesCount', { count: entities.length })}</span>
          </div>
        </div>

        {entities.length >= 5 && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step4.minimumMet')}
          </div>
        )}
      </div>

      <div className="manual-setup-section" style={{ marginTop: '32px' }}>
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          {t('step4.manualSetupNote')}
        </p>
      </div>
    </div>
  );
};

export default Step4BiologicalEntities;
